import { InboxOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Select, Spin } from 'antd';
import Header from 'components/Header/Header';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import {
  createPredictions,
  getAllSetting,
  getFP,
  getIP,
  getMatchList,
  getTime
} from 'service/base';
import { twMerge } from 'tailwind-merge';
import { CONST_SITE } from 'utils/common/const';

const daysOfWeek = {
  Monday: 'Thứ Hai',
  Tuesday: 'Thứ Ba',
  Wednesday: 'Thứ Tư',
  Thursday: 'Thứ Năm',
  Friday: 'Thứ Sáu',
  Saturday: 'Thứ Bảy',
  Sunday: 'Chủ Nhật'
};

const leagues = [
  {
    id: 1,
    name: 'UEFA Champions League',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/f/f5/UEFA_Champions_League.svg/1024px-UEFA_Champions_League.svg.png'
  },
  {
    id: 2,
    name: 'UEFA Europa League',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Europa_League_2021.svg/795px-Europa_League_2021.svg.png'
  },
  {
    id: 3,
    name: 'English Premier League',
    logo: 'https://loodibee.com/wp-content/uploads/Premier-League-Logo.png'
  },
  {
    id: 4,
    name: 'La Liga',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/LaLiga_logo_2023.svg/1200px-LaLiga_logo_2023.svg.png'
  },
  {
    id: 5,
    name: 'Italian Serie A',
    logo: 'https://1000logos.net/wp-content/uploads/2019/01/Italian-Serie-A-Logo.png'
  },
  {
    id: 6,
    name: 'German Bundesliga',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/df/Bundesliga_logo_%282017%29.svg/1200px-Bundesliga_logo_%282017%29.svg.png'
  },
  {
    id: 7,
    name: 'France Ligue 1',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Logo_Ligue_1_McDonald%27s_2024.svg/250px-Logo_Ligue_1_McDonald%27s_2024.svg.png'
  }
];

const PS99Page = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [dataSettings, setDataSettings] = useState([]);

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'tomorrow', 'day3', 'day4', 'day5'
  // Hàm tạo danh sách options cho Select
  const getDateOptions = () => {
    const now = dayjs();
    const options = [
      { value: 'all', label: 'Tất cả' },
      { value: 'today', label: `Hôm nay: ${now.format('DD/MM/YYYY')}` },
      { value: 'tomorrow', label: `Ngày mai: ${now.add(1, 'day').format('DD/MM/YYYY')}` },
      {
        value: 'day3',
        label: `${daysOfWeek[now.add(2, 'day').format('dddd')]}: ${now
          .add(2, 'day')
          .format('DD/MM/YYYY')}`
      },
      {
        value: 'day4',
        label: `${daysOfWeek[now.add(3, 'day').format('dddd')]}: ${now
          .add(3, 'day')
          .format('DD/MM/YYYY')}`
      },
      {
        value: 'day5',
        label: `${daysOfWeek[now.add(4, 'day').format('dddd')]}: ${now
          .add(4, 'day')
          .format('DD/MM/YYYY')}`
      }
    ];
    return options;
  };

  // Hàm lọc trận đấu theo tab được chọn
  const getFilteredMatches = () => {
    if (!listDataMatch || listDataMatch.length === 0) return [];

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const tomorrow = now.add(1, 'day').format('YYYY-MM-DD');
    const day3 = now.add(2, 'day').format('YYYY-MM-DD');
    const day4 = now.add(3, 'day').format('YYYY-MM-DD');
    const day5 = now.add(4, 'day').format('YYYY-MM-DD');

    switch (activeTab) {
      case 'today':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === today;
        });
      case 'tomorrow':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === tomorrow;
        });
      case 'day3':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day3;
        });
      case 'day4':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day4;
        });
      case 'day5':
        return listDataMatch.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day5;
        });
      case 'all':
      default:
        return listDataMatch;
    }
  };

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagues((prevSelectedLeagues) =>
      prevSelectedLeagues.includes(leagueId)
        ? prevSelectedLeagues.filter((id) => id !== leagueId)
        : [...prevSelectedLeagues, leagueId]
    );
  };

  const [scrollPosition, setScrollPosition] = useState(0);
  const leagueListRef = React.createRef();

  const scrollLeft = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: -100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft - 100);
    }
  };

  const scrollRight = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: 100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft + 100);
    }
  };

  useEffect(() => {
    fetchAllSetting();
    fetchIP();
    fetchFP();
    fetchTimeServer();
  }, []);

  useEffect(() => {
    fetchDataScheduByLS();
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.PS99
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setIsLoading(false);
    }
  };

  const fetchAllSetting = async (data) => {
    try {
      setIsLoading(true);
      const res = await getAllSetting(qs.stringify({ site: CONST_SITE.PS99 }));
      setDataSettings(res?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onFinishForm = async (data) => {
    try {
      const filteredMatches = getFilteredMatches();
      if (filteredMatches?.length === 0) return;

      if (!data?.account) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập!'
        });
        return;
      }

      // Validate that both home and away scores are provided for each match
      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        // Bỏ qua field account và các field không phải score
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        // Kiểm tra nếu một trong hai score có giá trị nhưng score kia không có
        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        if (hasValue && !hasOtherValue) {
          return true;
        }

        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỉ số cho mỗi trận đấu!'
        });
        return;
      }

      setIsLoadingSubmit(true);

      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng dự đoán ít nhất một trận đấu!'
        });
        setIsLoadingSubmit(false);
        return;
      }

      const res = await createPredictionsWithTimeout(resultGroup);
      if (res?.code === 0) {
        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    if (!errorInfo?.values?.account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
    } else {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng điền đầy đủ thông tin!'
      });
    }
  };

  const onFinishCountDown = () => {
    console.log('Countdown finished');
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore'].includes(scoreType)) return acc;

      // Kiểm tra giá trị hợp lệ
      if (value === undefined || value === null || value === '') return acc;

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined
          // sameScore: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else if (scoreType === 'awayScore') {
        acc[matchId].awayScore = value;
      }
      // else if (scoreType === 'sameScore') {
      //   acc[matchId].sameScore = value;
      // }

      return acc;
    }, {});

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          // match.sameScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          // match.sameScore !== null &&
          match.homeScore !== '' &&
          // match.sameScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );

        return {
          matchId,
          homeScore,
          awayScore,
          // sameScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const showListItemMatch = (matchList) => {
    if (!matchList || matchList.length <= 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <InboxOutlined className="text-6xl text-gray-300 mb-4" />
          <p className="text-center text-gray-500">Không có trận đấu nào để dự đoán</p>
        </div>
      );
    } else {
      const filteredMatchList =
        selectedLeagues.length > 0
          ? matchList.filter((item) =>
              selectedLeagues.includes(
                leagues.find((league) => league.name === item.leagueName)?.id
              )
            )
          : matchList;

      const filteredByTime = filteredMatchList.filter((item) => timeServer <= item.countdown);

      const itemMatch = filteredByTime.map((item, index) => {
        // const matchDayOfWeek = daysOfWeek[dayjs(item.startTime * 1000).format('dddd')]; // Calculate day of week for each match
        return (
          <div
            key={index}
            className="relative flex flex-col lg:flex-row items-center px-4  md:py-3 pb-3 w-full rounded-2xl shadow-md md:shadow-none bg-gradient-to-b md:bg-gradient-to-b from-white to-[#C8EFFF] md:from-transparent md:to-transparent"
          >
            <div className="flex bg-gradient-to-b from-[#4B4B4C] to-[#000] rounded-b-2xl md:bg-none md:rounded-none flex-row w-full max-w-[200px] items-center px-4 md:px-2 py-1 justify-center md:justify-start shrink-0 ">
              <span className="flex items-center h-full font-semibold text-sm md:text-base text-white md:text-black">
                {item.leagueName}
              </span>
            </div>
            {/* Date/Time Section */}
            <div className="flex items-center w-24 shrink-0">
              <div className="font-normalflex-col leading-5 text-base text-black text-center">
                <p className="mb-0">{dayjs(item.startTime * 1000).format('HH:mm')}</p>
                <p className="mb-0">{dayjs(item.startTime * 1000).format('DD/MM/YYYY')}</p>
              </div>
            </div>

            {/* Thời gian dự đoán còn lại */}
            {/* <div className="flex flex-row items-center ">
                <span className="flex flex-row lg:flex-col xl:flex-row items-center h-full">
                  <span
                    className={`mr-1 flex items-center h-full md:text-[16px] text-[14px] ${
                      timeServer > item.countdown ? 'text-red-500' : 'text-black'
                    }`}
                  >
                    {timeServer > item.countdown ? 'Hết giờ' : 'Thời gian dự đoán còn: '}
                  </span>
                  <Statistic.Countdown
                    value={item.countdown}
                    onFinish={onFinishCountDown}
                    className="text-[#26A9E0] ml-1 lg:mt-[2px] font-semibold flex items-center"
                  />
                </span>
              </div> */}

            {/* Teams Section */}
            <div className="flex-1 flex gap-2 md:gap-3 items-center justify-center w-full min-w-0">
              {/* Home Team */}
              <div className="flex-1 flex flex-col-reverse md:flex-row gap-2 items-center md:items-center justify-end min-w-0">
                <p className="font-bold leading-5 text-sm md:text-base text-black line-clamp-2 text-center md:text-right">
                  {item.homeName}
                </p>
                <div className="border shrink-0 w-10 h-10 overflow-hidden flex items-center justify-center">
                  <img
                    alt={item.homeName}
                    className="w-full h-full object-contain"
                    src={item.homeLogo}
                  />
                </div>
              </div>

              {/* VS Divider */}
              <div className="flex flex-row items-center gap-2 justify-center">
                <Form.Item
                  className="w-[52px] md:w-[70px] h-[34px] flex items-center justify-center mb-0"
                  name={`homeScore_${item.matchId}`}
                >
                  <InputNumber
                    disabled={timeServer > item.countdown ? true : false}
                    controls={false}
                    min={0}
                    max={99}
                    maxLength={2}
                    placeholder="-"
                    className="bg-[#DCDFEE] border-0 w-[52px] md:w-[70px] h-[34px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] rounded-[10px] px-[10px] py-[5px]"
                    style={{
                      textAlign: 'center',
                      fontFamily: "'Roboto', sans-serif"
                    }}
                  />
                </Form.Item>

                {/* Soccer Ball Icon */}
                <div className="w-2 h-5 shrink-0 flex items-center justify-center font-bold text-2xl text-black">
                  :
                </div>

                {/* VS Divider */}
                <Form.Item
                  className="w-[52px] md:w-[70px] h-[34px] flex items-center justify-center mb-0"
                  name={`awayScore_${item.matchId}`}
                >
                  <InputNumber
                    disabled={timeServer > item.countdown ? true : false}
                    controls={false}
                    min={0}
                    max={99}
                    maxLength={2}
                    placeholder="-"
                    className="bg-[#DCDFEE] border-0 w-[52px] md:w-[70px] h-[34px] text-center text-xs sm:text-sm md:text-base font-normal text-[#505050] rounded-[10px] px-[10px] py-[5px]"
                    style={{
                      textAlign: 'center',
                      fontFamily: "'Roboto', sans-serif"
                    }}
                  />
                </Form.Item>
              </div>

              {/* Away Team */}
              <div className="flex-1 flex flex-col md:flex-row gap-2 items-center md:items-center justify-start min-w-0">
                <div className="border border-gray-300 rounded-full shrink-0 w-10 h-10 overflow-hidden flex items-center justify-center bg-white">
                  <img
                    alt={item.awayName}
                    className="w-full h-full object-contain"
                    src={item.awayLogo}
                  />
                </div>
                <p className="font-bold leading-5 text-sm md:text-base text-black line-clamp-2 text-center md:text-left">
                  {item.awayName}
                </p>
              </div>
            </div>

            {/* Prediction Button */}
            <div className="flex items-center mt-2 lg:mt-0 gap-1 justify-center lg:justify-end w-[300px] shrink-0">
              {/* <button
                  type="primary"
                  htmlType="submit"
                  disabled={isLoadingSubmit}
                  onClick={() => {
                    // Scroll to form or handle prediction action
                    const formElement = document.querySelector('form[name="formMatch"]');
                    if (formElement) {
                      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="bg-white border-2 border-[#ff8900] px-3 py-1 md:px-4 md:py-1 rounded-full hover:bg-[#ff8900] hover:text-white transition-colors cusorsor-pointer"
                >
                  <p className="font-semibold leading-6 text-xs md:text-sm text-[#ff8900] hover:text-white mb-0">
                    Dự đoán ngay
                  </p>
                </button> */}
              {/* {item.linkPS99 && item.linkPS99 !== '' && ( */}
              <a
                href={item.linkPS99 || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#DD0000] hover:bg-[#e83333] w-24 px-3 py-1 md:px-4 md:py-1 rounded-full hover:text-white transition-colors cursor-pointer no-underline inline-block"
              >
                <p className="font-semibold leading-6 text-xs md:text-sm text-center text-[#fff] hover:text-white mb-0">
                  Hot
                </p>
              </a>
              {/* )} */}
              {/* {item.linkCaKhiaTV && item.linkCaKhiaTV !== '' && ( */}
              <a
                href={item.linkCaKhiaTV || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#FF6A00] w-24 border-2 px-3 py-1 md:px-4 md:py-1 rounded-full hover:bg-[#ff8900] hover:text-white transition-colors cursor-pointer no-underline inline-block"
              >
                <p className="font-semibold leading-6 text-xs md:text-sm text-center text-[#fff] hover:text-white/80 mb-0">
                  Xem Live
                </p>
              </a>
              {/* )} */}
            </div>
            {filteredByTime.length - 1 !== index && (
              <div className="hidden md:block absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[90%] h-[1px] bg-[#C0C9DD] mt-5"></div>
            )}
          </div>
        );
      });
      if (!itemMatch || itemMatch.length <= 0) {
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <InboxOutlined className="text-6xl text-gray-300 mb-4" />
            <p className="text-center text-gray-500">Không có trận đấu nào để dự đoán</p>
          </div>
        );
      } else {
        return itemMatch;
      }
    }
  };

  return (
    <div className={twMerge(SITE, 'w-full h-full')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />
      <div className="container relative z-10 pb-[24px] md:pb-[32px] px-2">
        <Header />

        <div
          className="mx-auto max-w-[1320px] bg-white/95 border-2 border-solid shadow shadow-primary p-2 w-full md:pb-8 md:pt-4 rounded-3xl"
          style={{
            background: '#FFFFFF80',
            borderColor: '#26A9E0',
            borderRadius: '1.5rem'
          }}
        >
          {/* <div className="flex space-x-6 mt-4 justify-center items-center">
            <Button
              type="link"
              icon={<LeftOutlined />}
              onClick={scrollLeft}
              className="text-black  !px-0 !mx-0"
            />
            <div
              className="flex overflow-x-auto scrollbar-custom pb-2 !mx-0"
              ref={leagueListRef}
              style={{ scrollBehavior: 'smooth' }}
            >
              {leagues.map((league) => (
                <div
                  key={league.id}
                  className={twMerge(
                    'flex justify-center items-center p-1 mx-2 rounded-lg cursor-pointer transition-all duration-300 hover:scale-105',
                    selectedLeagues.includes(league.id)
                      ? 'bg-gradient-to-br from-[#26A9E0] to-[#63c8f2] shadow-lg shadow-[#63c8f2] border-2 border-[#26A9E0]'
                      : 'bg-white/80 backdrop-blur-sm border-2 border-[#26A9E0]/30 hover:border-[#26A9E0] hover:shadow-md'
                  )}
                  style={{ width: '80px', minWidth: '80px', border: '1px solid #26A9E0' }}
                  onClick={() => handleLeagueClick(league.id)}
                >
                  <img src={league.logo} alt={league.name} className="h-12 object-contain" />
                </div>
              ))}
            </div>
            <Button
              type="link"
              icon={<RightOutlined />}
              onClick={scrollRight}
              className="text-black !px-0 !mx-0"
            />
          </div> */}

          <div className="relative w-full h-full">
            <Form
              name="formMatch"
              onFinish={onFinishForm}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              form={form}
              initialValues={{ account: '' }}
            >
              <div className="w-full h-full">
                {isLoading ? (
                  <div className="w-full h-full text-center py-8">
                    <Spin />
                  </div>
                ) : (
                  <div className="flex flex-col w-full h-full items-center mt-2">
                    <div className="flex w-full h-full items-center justify-between flex-row gap-2 md:mx-3 md:flex-row">
                      <div className="w-full md:w-auto md:px-0 md:ml-3">
                        <div className="flex items-center justify-start w-full min-w-[160px] max-w-[168px] md:max-w-[220px]">
                          <Select
                            value={activeTab}
                            onChange={(value) => setActiveTab(value)}
                            options={getDateOptions()}
                            className="custom-select w-full h-[36px] rounded-full"
                            popupClassName="w-[300px] custom-select-dropdown"
                          />
                        </div>
                      </div>
                      <div className="flex py-1 md:py-2">
                        <Form.Item name="account" className="mb-0">
                          <Input
                            placeholder="Nhập tên đăng nhập ..."
                            maxLength={20}
                            className="h-[36px] w-[116px] md:w-[200px] rounded-l-[30px] rounded-r-none border-[#c9c9c9] text-[12px] px-[17px] py-[10px]"
                            style={{
                              borderRight: 'none'
                            }}
                            rules={[
                              {
                                required: true,
                                message: 'Vui lòng điền tên đăng nhập!'
                              }
                            ]}
                          />
                        </Form.Item>
                        <Form.Item className="mb-0 items-center justify-center">
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoadingSubmit}
                            className="text-white font-semibold rounded-l-none rounded-r-[30px] border-0 h-[36px] w-[80px] md:w-[124px] text-[12px] md:text-[14px]"
                            style={{
                              background: '#26A9E0'
                            }}
                          >
                            Xác nhận
                          </Button>
                        </Form.Item>
                      </div>
                    </div>
                    <div className="w-full h-full flex flex-col gap-2 md:gap-0 mt-2">
                      {showListItemMatch(getFilteredMatches())}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mt-2"></div>
              </div>
            </Form>
          </div>
        </div>
        {/* Lịch Thi Đấu */}
        <div
          className="mx-auto max-w-[1320px] bg-white/95 border-2 border-solid shadow shadow-primary mt-4 p-2 w-full md:pb-4 md:pt-4 rounded-3xl"
          style={{
            background: '#FFFFFF80',
            borderColor: '#26A9E0',
            borderRadius: '1.5rem'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-center p-3">
            <img
              src="/assets/images/match/plan.png"
              alt="Lịch thi đấu hot"
              className="h-auto max-h-[60px] object-contain"
            />
          </div>

          {/* Content */}
          <div className="p-3 border-none border-0">
            <iframe
              src="https://football-widget.pages.dev/"
              //   width="100%"
              height="600"
              //   frameBorder="0"
              //   allowFullScreen
              title="Football Widget"
              className="w-full border-0 border-none rounded-lg"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PS99Page;
